import { useState } from 'react'
import { motion } from 'framer-motion'
import { 
  TrendingUp, 
  TrendingDown, 
  Download, 
  Filter,
  PieChart,
  BarChart3
} from 'lucide-react'
import { useStore } from '@/store'
import { formatCurrency, formatPercentage, formatDate } from '@/lib/utils'
import { mockSpendingCategories } from '@/data/mockData'
import Button from '@/components/ui/Button'
import { toast } from 'sonner'

export default function Treasury() {
  const portfolio = useStore(state => state.portfolio)
  const transactions = useStore(state => state.transactions)
  
  const [timeRange, setTimeRange] = useState<'7d' | '30d' | '90d' | '1y'>('30d')
  
  const totalValue = portfolio?.totalValue || 0
  const change24h = portfolio?.change24h || 0
  const change24hPercent = portfolio?.change24hPercent || 0
  const isPositive = change24h >= 0
  
  const handleExport = () => {
    toast.info('Export feature coming soon!')
  }
  
  return (
    <div className="space-y-6 pb-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Treasury</h1>
        <Button variant="secondary" size="sm" onClick={handleExport}>
          <Download className="w-4 h-4 mr-1" />
          Export
        </Button>
      </div>
      
      {/* Portfolio Value */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative overflow-hidden rounded-3xl bg-gradient-card border border-border p-6"
      >
        <div className="absolute -top-20 -right-20 w-40 h-40 bg-brand/20 rounded-full blur-3xl" />
        <div className="absolute -bottom-20 -left-20 w-40 h-40 bg-accent-cyan/10 rounded-full blur-3xl" />
        
        <div className="relative z-10">
          <p className="text-text-secondary text-sm mb-2">Portfolio Value</p>
          <p className="text-balance text-text-primary">
            {formatCurrency(totalValue)}
          </p>
          
          <div className={`flex items-center gap-2 mt-4 ${isPositive ? 'text-accent-green' : 'text-accent-red'}`}>
            {isPositive ? (
              <TrendingUp className="w-5 h-5" />
            ) : (
              <TrendingDown className="w-5 h-5" />
            )}
            <span className="font-medium">
              {isPositive ? '+' : ''}{formatCurrency(change24h)}
            </span>
            <span className="text-text-tertiary">
              ({isPositive ? '+' : ''}{formatPercentage(change24hPercent)})
            </span>
            <span className="text-text-tertiary ml-1">24h</span>
          </div>
        </div>
      </motion.div>
      
      {/* Time Range Selector */}
      <div className="flex gap-2">
        {(['7d', '30d', '90d', '1y'] as const).map((range) => (
          <button
            key={range}
            onClick={() => setTimeRange(range)}
            className={`flex-1 py-2 px-3 rounded-xl text-sm font-medium transition-all ${
              timeRange === range
                ? 'bg-brand text-white'
                : 'bg-background-elevated text-text-secondary hover:text-text-primary'
            }`}
          >
            {range === '7d' && '7 Days'}
            {range === '30d' && '30 Days'}
            {range === '90d' && '90 Days'}
            {range === '1y' && '1 Year'}
          </button>
        ))}
      </div>
      
      {/* Asset Allocation */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <PieChart className="w-5 h-5 text-brand" />
            Asset Allocation
          </h2>
        </div>
        
        <div className="p-4 rounded-2xl bg-background-elevated border border-border">
          <div className="space-y-4">
            {portfolio?.assets.map((asset) => (
              <div key={asset.symbol} className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                    asset.symbol === 'USDC' ? 'bg-green-500/20 text-green-500' :
                    asset.symbol === 'USDT' ? 'bg-teal-500/20 text-teal-500' :
                    'bg-purple-500/20 text-purple-500'
                  }`}>
                    <span className="font-bold text-sm">{asset.symbol[0]}</span>
                  </div>
                  <div>
                    <p className="font-medium">{asset.symbol}</p>
                    <p className="text-sm text-text-tertiary">{asset.name}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-medium">{formatCurrency(asset.value)}</p>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-text-tertiary">{asset.allocation.toFixed(1)}%</span>
                    {asset.change24h !== 0 && (
                      <span className={`text-xs ${asset.change24h > 0 ? 'text-accent-green' : 'text-accent-red'}`}>
                        {asset.change24h > 0 ? '+' : ''}{formatPercentage(asset.change24h)}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Spending by Category */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-accent-cyan" />
            Spending by Category
          </h2>
          <Button variant="ghost" size="sm">
            <Filter className="w-4 h-4 mr-1" />
            Filter
          </Button>
        </div>
        
        <div className="p-4 rounded-2xl bg-background-elevated border border-border">
          <div className="space-y-4">
            {mockSpendingCategories.map((category) => (
              <div key={category.name} className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="font-medium">{category.name}</span>
                  <span className="text-text-secondary">
                    {formatCurrency(category.amount)}
                  </span>
                </div>
                <div className="h-2 bg-background rounded-full overflow-hidden">
                  <div 
                    className="h-full rounded-full transition-all duration-500"
                    style={{ 
                      width: `${category.percentage}%`,
                      backgroundColor: category.color
                    }}
                  />
                </div>
                <p className="text-xs text-text-tertiary">
                  {category.percentage}% of total spending
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Recent Treasury Transactions */}
      <section>
        <h2 className="text-lg font-semibold mb-4">Recent Transactions</h2>
        <div className="space-y-1">
          {transactions.slice(0, 5).map((tx) => (
            <div 
              key={tx.id}
              className="flex items-center justify-between p-3 rounded-xl hover:bg-background-elevated transition-colors"
            >
              <div>
                <p className="font-medium">{tx.description || tx.type}</p>
                <p className="text-sm text-text-tertiary">
                  {formatDate(tx.timestamp, { format: 'relative' })}
                </p>
              </div>
              <div className="text-right">
                <p className={`font-semibold ${tx.amount > 0 ? 'text-accent-green' : 'text-text-primary'}`}>
                  {tx.amount > 0 ? '+' : ''}{formatCurrency(Math.abs(tx.amount))}
                </p>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  )
}